import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { FirestoreError } from "firebase/firestore";
import { useAuth } from "../hooks/useAuth";
import {
  subscribeToFollowedLists,
  subscribeToUserLists,
  type UserList,
} from "../features/lists/services/listsService";

function sortLists(lists: UserList[]): UserList[] {
  return [...lists].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    const ta = a.updatedAt?.toMillis?.() ?? a.createdAt?.toMillis?.() ?? 0;
    const tb = b.updatedAt?.toMillis?.() ?? b.createdAt?.toMillis?.() ?? 0;
    return tb - ta;
  });
}

interface ListsContextData {
  lists: UserList[];
  owned: UserList[];
  followed: UserList[];
  isLoading: boolean;
  isSyncEnabled: boolean;
  enableListsSync: () => void;
}

const ListsContext = createContext<ListsContextData | null>(null);

export function ListsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [owned, setOwned] = useState<UserList[]>([]);
  const [followed, setFollowed] = useState<UserList[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncEnabled, setIsSyncEnabled] = useState(false);
  const enableListsSync = useCallback(() => setIsSyncEnabled(true), []);

  useEffect(() => {
    if (!user?.uid) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset sincrono pre-subscription (padrao anterior ao react-hooks v7); refactor de timing rastreado na auditoria 03/08 par.4.1
      setOwned([]);
      setFollowed([]);
      setIsLoading(false);
      setIsSyncEnabled(false);
      return;
    }

    if (!isSyncEnabled) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let ownedReady = false;
    let followedReady = false;

    const finish = () => {
      if (ownedReady && followedReady) setIsLoading(false);
    };

    const onErr = (err: Error) => {
      const isPermDenied =
        (err instanceof FirestoreError && err.code === "permission-denied") ||
        ((err as { code?: string }).code === "permission-denied");
      if (isPermDenied) {
        // ⚠️ Query de LISTA não é "filtrada" pelas rules: ou a rule é provável
        // pela query e ela passa inteira, ou é negada inteira. Foi assim que a
        // query de listas seguidas ficou morta para todo mundo até 03/09
        // (BUG-202) enquanto esta linha dizia que era filtro. Se isto aparecer
        // de novo, a rule de `user_lists` deixou de ser provável pela query.
        console.error("[Lura] ListsContext: query de listas negada pelas rules (BUG-202).");
      } else {
        console.error("[Lura] ListsContext error:", err.message);
      }
      setIsLoading(false);
    };

    const unsubOwned = subscribeToUserLists(
      user.uid,
      (data) => {
        setOwned(data);
        ownedReady = true;
        finish();
      },
      onErr,
    );

    const unsubFollowed = subscribeToFollowedLists(
      user.uid,
      (data) => {
        setFollowed(data);
        followedReady = true;
        finish();
      },
      onErr,
    );

    return () => {
      unsubOwned();
      unsubFollowed();
    };
  }, [isSyncEnabled, user?.uid]);

  const lists = useMemo(() => {
    const map = new Map<string, UserList>();
    for (const l of owned) map.set(l.id, l);
    for (const l of followed) map.set(l.id, l);
    return sortLists([...map.values()]);
  }, [owned, followed]);

  const value = useMemo(
    () => ({
      lists,
      owned,
      followed,
      isLoading,
      isSyncEnabled,
      enableListsSync,
    }),
    [lists, owned, followed, isLoading, isSyncEnabled, enableListsSync],
  );

  return <ListsContext.Provider value={value}>{children}</ListsContext.Provider>;
}

export function useListsContext(): ListsContextData {
  const ctx = useContext(ListsContext);
  if (!ctx) throw new Error("useListsContext requer ListsProvider como ancestral");
  return ctx;
}
